import React, { useState, useEffect } from "react";
import { styled } from "@mui/material/styles";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import {
    Table,
    TableBody,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TablePagination,
    CircularProgress,
    Box,
    IconButton,
    Checkbox,
    Typography,
    Button,
    Tooltip,
    Dialog
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import UploadCsvModal from "./UploadCsvModal";
import { verifyMentorPractices } from "../../../services/mentorServices/LearnerPracticeServices";
import { toast } from "react-toastify";
import Papa from "papaparse";
import "./LearnerPractices.css";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: "#7e7e7e",
        color: theme.palette.common.white,
        textTransform: "capitalize",
        fontSize: 14,
        fontWeight: 600,
        letterSpacing: "0.5px",
        borderBottom: "1px solid #e0e0e0",
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
        color: "#333",
        borderBottom: "1px solid #e0e0e0",
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    "&:nth-of-type(odd),&:nth-of-type(even)": {
        backgroundColor: theme.palette.common.white,
    },
    "&:last-child td, &:last-child th": {
        border: 0,
    },
    "&.Mui-selected, &.Mui-selected:hover": {
        backgroundColor: "rgba(25, 118, 210, 0.08)",
    }
}));

const LearnerPracticesTable = ({ data, loading, totalCount, currentPage = 1, selectedPracticeName, onPageChange, onRowsPerPageChange, onRefresh, onDownloadCSV, downloadingCSV = false, isAdmin = false, sortFilterNode }) => {
    const [page, setPage] = useState(currentPage ? currentPage - 1 : 0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [selectedRows, setSelectedRows] = useState([]);
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    const [verifying, setVerifying] = useState(false);
    const [isVerifyModalOpen, setIsVerifyModalOpen] = useState(false);
    const navigate = useNavigate();
    const { coeName } = useParams();

    const getBasePracticePath = () => {
        if (coeName) return `/${coeName}/learner-practices`;
        const user = JSON.parse(localStorage.getItem('user')) || {};
        if (user?.role === 'coe_report_viewer') {
            const formattedCoeName = user?.coeName ? user.coeName.trim().replace(/\s+/g, '-') : (user?.coe_name ? user.coe_name.trim().replace(/\s+/g, '-') : 'coe');
            return `/${formattedCoeName}/learner-practices`;
        }
        return isAdmin ? '/admin/learner-practices' : '/mentor/learner-practices';
    };

    useEffect(() => {
        if (currentPage !== undefined && currentPage !== null) {
            setPage(currentPage - 1);
        }
    }, [currentPage]);

    useEffect(() => {
        setPage(0);
    }, [selectedPracticeName]);

    useEffect(() => {
        if (totalCount !== undefined && totalCount > 0 && page * rowsPerPage >= totalCount) {
            setPage(0);
        }
    }, [totalCount, page, rowsPerPage]);

    const selectableRows = (data || []).filter(row => (row.status || '').toLowerCase() === 'verification_pending');

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
        if (onPageChange) {
            onPageChange(newPage + 1, rowsPerPage);
        }
    };

    const handleChangeRowsPerPage = (event) => {
        const newLimit = parseInt(event.target.value, 10);
        setRowsPerPage(newLimit);
        setPage(0);
        if (onRowsPerPageChange) {
            onRowsPerPageChange(1, newLimit);
        }
    };

    const handleSelectAll = (event) => {
        if (event.target.checked) {
            const newSelecteds = selectableRows.map((n) => {
                const index = (data || []).indexOf(n);
                return n.PracticeProgressId || n.submissionId || n.submission_id || n.id || `sub_${index}`;
            });
            setSelectedRows(newSelecteds);
            return;
        }
        setSelectedRows([]);
    };

    const handleSelectRow = (event, id) => {
        const selectedIndex = selectedRows.indexOf(id);
        let newSelected = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selectedRows, id);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selectedRows.slice(1));
        } else if (selectedIndex === selectedRows.length - 1) {
            newSelected = newSelected.concat(selectedRows.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(
                selectedRows.slice(0, selectedIndex),
                selectedRows.slice(selectedIndex + 1),
            );
        }
        setSelectedRows(newSelected);
    };

    const paginatedRows = onPageChange ? data : (data || []).slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    const getStatusChipClass = (status) => {
        switch ((status || '').toLowerCase()) {
            case 'Practice_pending':
            case 'lc_pending':
            case 'verification_pending':
            case 'pending': return 'pending';
            case 'evaluation_pending': return 'evaluation-pending';
            case 'evaluation_failed': return 'evaluation-failed';
            case 'completed':
            case 'Practiceed': return 'Practiceed';
            case 'in progress': return 'in-progress';
            default: return 'pending';
        }
    };



    const formatDate = (dateString) => {
        if (!dateString) return { date: "-", time: "" };
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return { date: dateString, time: "" };

        const optionsDate = { month: 'short', day: 'numeric', year: 'numeric' };
        const dateFormatted = date.toLocaleDateString('en-US', optionsDate);

        const optionsTime = { hour: 'numeric', minute: '2-digit', hour12: true };
        const timeFormatted = date.toLocaleTimeString('en-US', optionsTime);

        return {
            date: dateFormatted,
            time: timeFormatted
        };
    };

    const handleDownloadCSV = () => {
        if (onDownloadCSV) {
            onDownloadCSV();
            return;
        }

        if (!data || data.length === 0) {
            toast.info("No learner practice data available to download.");
            return;
        }

        const formattedData = data.map((item, index) => {
            const learnerName = item.learnerName || item.learner_name || item?.Learner?.name || item?.learner?.name || item?.name || "-";
            const emailId = item.emailId || item.email_id || item?.Learner?.emailId || item?.Learner?.email || item?.Learner?.email_id || item?.learner?.email || item?.email || item.institutionEmail || "-";
            const labName = item.labName || item.lab_name || item?.Learner?.labName || item?.Learner?.lab_name || item?.learner?.lab_name || item?.learner?.labName || "-";
            const rollNo = item.rollNo || item.rollNumber || item.roll_number || item?.Learner?.rollNumber || item?.Learner?.roll_number || item?.Learner?.rollNo || item?.Learner?.rollNum || item?.learner?.roll_num || item?.roll_num || item?.rollNum || item.learnerCode || "-";
            const practiceTitle = item.practiceTitle || "-";
            const practiceName = item.practiceName || item.PracticeName || item.PracticeTitle || item.Practice_name || "-";
            const practiceLevel = item.practiceLevel || "-";
            
            const subDateRaw = item.submissionDate || item.submission_date || item.created_at || item.submittedDate || item.submitted_date;
            const subDate = subDateRaw ? new Date(subDateRaw).toLocaleString('en-US') : "-";

            const qReady = item?.gradingStatus?.quiz === 'ready' ? 'ready' : 'pending';
            const tReady = item?.gradingStatus?.technical === 'ready' ? 'ready' : 'pending';
            const cReady = item?.gradingStatus?.coding === 'ready' ? 'ready' : 'pending';
            const gradingStatusStr = `Q: ${qReady} | T: ${tReady} | C: ${cReady}`;

            const status = (item.status || "PENDING").replace(/_/g, ' ');

            return {
                "S.No": index + 1,
                "Roll Number": rollNo,
                "Learner Name": learnerName,
                "Email": emailId,
                "Lab Name": labName,
                "Practice Name": practiceName,
                "Level": practiceLevel,
                "Submission Date": subDate,
                "Status": status
            };
        });

        const csvContent = Papa.unparse(formattedData);
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        const cleanName = (selectedPracticeName && selectedPracticeName !== 'all')
            ? selectedPracticeName.replace(/[^a-zA-Z0-9]/g, '_')
            : 'All_Practices';
        link.setAttribute("download", `Learner_Practice_Scores_${cleanName}_${new Date().toISOString().slice(0, 10)}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        toast.success("CSV file downloaded successfully!");
    };

    const handleMarkAsVerified = async () => {
        if (selectedRows.length === 0) return;
        setVerifying(true);
        try {
            const PracticesToVerify = selectedRows.map(id => {
                const row = (data || []).find((r, index) => {
                    const rId = r.PracticeProgressId || r.submissionId || r.submission_id || r.id || `sub_${index}`;
                    return rId === id;
                });
                return {
                    learnerId: row?.learnerId,
                    PracticeProgressId: id
                };
            }).filter(item => item.learnerId && item.PracticeProgressId);

            const payload = {
                Practices: PracticesToVerify
            };

            const res = await verifyMentorPractices(payload);
            if (res?.data?.success) {
                toast.success("Selected Practices marked as verified successfully.");
                setSelectedRows([]);
                setIsVerifyModalOpen(false);
                if (onRefresh) onRefresh();
            } else {
                toast.error(res?.data?.message || "Failed to verify Practices.");
            }
        } catch (error) {
            console.error("Verify error:", error);
            toast.error(error?.response?.data?.message || "Error verifying Practices.");
        } finally {
            setVerifying(false);
        }
    };

    return (
        <Box sx={{ width: "100%" }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, px: 1, flexWrap: 'wrap', gap: 2 }}>
                <Typography sx={{ color: '#1976d2', fontWeight: 700, fontSize: '14px', width: { xs: '100%', sm: 'auto' } }}>
                    Total Practices: {totalCount !== undefined ? totalCount : (data?.length || 0)}
                </Typography>
                <Box sx={{ display: 'flex', gap: '12px', flexWrap: 'wrap', width: { xs: '100%', sm: 'auto' } }}>
                    {data && data.length > 0 && (
                        <Button
                            variant="contained"
                            color="success"
                            onClick={handleDownloadCSV}
                            disabled={downloadingCSV}
                            sx={{ borderRadius: '4px', textTransform: 'none', fontWeight: 600, padding: '6px 16px', width: { xs: '100%', sm: 'auto' } }}
                            startIcon={downloadingCSV ? <CircularProgress size={18} color="inherit" /> : <DownloadIcon sx={{ fontSize: '18px' }} />}
                        >
                            {downloadingCSV ? "Downloading..." : "Download Practice CSV"}
                        </Button>
                    )}
                    {sortFilterNode && (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {sortFilterNode}
                        </Box>
                    )}


                </Box>
            </Box>

            <TableContainer component={Paper} className="table-container" sx={{ width: "100%", overflowX: "auto", boxShadow: 'none', border: '1px solid #e0e0e0', borderRadius: '8px' }}>
                <Table sx={{ tableLayout: "auto", width: "max-content", minWidth: "100%" }} aria-label="customized table">
                    <TableHead>
                        <TableRow>
                            <StyledTableCell>ROLL NUMBER</StyledTableCell>
                            <StyledTableCell>LEARNER NAME</StyledTableCell>
                            <StyledTableCell>EMAIL</StyledTableCell>
                            <StyledTableCell>LAB NAME</StyledTableCell>
                            <StyledTableCell>PRACTICE NAME</StyledTableCell>
                            <StyledTableCell>LEVEL</StyledTableCell>
                            <StyledTableCell>SUBMISSION DATE</StyledTableCell>
                            <StyledTableCell>STATUS</StyledTableCell>
                            <StyledTableCell align="right">ACTIONS</StyledTableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <StyledTableCell colSpan={8} align="center">
                                    <Box display="flex" justifyContent="center" alignItems="center" height="100px">
                                        <CircularProgress />
                                    </Box>
                                </StyledTableCell>
                            </TableRow>
                        ) : paginatedRows?.length > 0 ? (
                            paginatedRows.map((row, index) => {
                                const rollNo = row.rollNo || row.roll_number || row?.Learner?.rollNo || row?.Learner?.roll_number || row?.Learner?.rollNum || row?.learner?.roll_num || row?.roll_num || row?.rollNum || row.learnerCode || "-";
                                const learnerName = row.learnerName || row.learner_name || row?.Learner?.name || row?.learner?.name || row?.name || "-";
                                const emailId = row.emailId || row.email_id || row?.Learner?.emailId || row?.Learner?.email || row?.Learner?.email_id || row?.learner?.email || row?.email || row.institutionEmail || "-";
                                const labName = row.labName || row.lab_name || row?.Learner?.labName || row?.Learner?.lab_name || row?.learner?.lab_name || row?.learner?.labName || "-";
                                const practiceTitle = row.practiceTitle || "-";
                                const practiceName = row.practiceName || row.PracticeName || row.PracticeTitle || "-";
                                const practiceLevel = row.practiceLevel || "-";
                                
                                const subDateRaw = row.submissionDate || row.submission_date || row.created_at || row.submittedDate || row.submitted_date;
                                const { date: subDate, time: subTime } = formatDate(subDateRaw);
                                const status = row.status || "Pending";
                                const globalIndex = page * rowsPerPage + index;
                                const subId = row.practiceProgressId || row.PracticeProgressId || row.submissionId || row.submission_id || row.id || `sub_${globalIndex}`;
                                const learnerId = row.learnerId;

                                const qReady = row?.gradingStatus?.quiz === 'ready' ? 'ready' : 'pending';
                                const tReady = row?.gradingStatus?.technical === 'ready' ? 'ready' : 'pending';
                                const cReady = row?.gradingStatus?.coding === 'ready' ? 'ready' : 'pending';
                                const gradingStatusStr = `Q: ${qReady} | T: ${tReady} | C: ${cReady}`;

                                const isVerificationPending = (status || '').toLowerCase() === 'verification_pending';
                                const isSelected = selectedRows.indexOf(subId) !== -1;

                                return (
                                    <StyledTableRow key={index} hover>
                                        <StyledTableCell>{rollNo}</StyledTableCell>
                                        <StyledTableCell sx={{ fontWeight: 500 }}>
                                            {learnerName}
                                        </StyledTableCell>
                                        <StyledTableCell>{emailId}</StyledTableCell>
                                        <StyledTableCell>{labName}</StyledTableCell>
                                        <StyledTableCell>{practiceName}</StyledTableCell>
                                        <StyledTableCell>{practiceLevel}</StyledTableCell>
                                        <StyledTableCell>
                                            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: "center", gap: 1 }}>
                                                <span>{subDate}</span>
                                                {subTime && <span style={{ fontSize: '12px', color: '#6c757d' }}>{subTime}</span>}
                                            </Box>
                                        </StyledTableCell>
                                        <StyledTableCell>
                                            <span className={`status-chip ${getStatusChipClass(status)}`}>
                                                {status.replace(/_/g, ' ')}
                                            </span>
                                        </StyledTableCell>
                                        <StyledTableCell align="center">
                                            <Typography 
                                                component="span" 
                                                onClick={() => navigate(`${getBasePracticePath()}/${subId}`, { state: { learnerId } })}
                                                sx={{ color: '#1976d2', fontWeight: 600, cursor: 'pointer', textDecoration: 'underline', '&:hover': { color: '#115293' }, fontSize: '13.5px' }}
                                            >
                                                View
                                            </Typography>
                                        </StyledTableCell>
                                    </StyledTableRow>
                                );
                            })
                        ) : (
                            <StyledTableRow>
                                <StyledTableCell colSpan={8} align="center" sx={{ fontWeight: "600", color: "grey", height: "100px" }}>
                                    No Practices found.
                                </StyledTableCell>
                            </StyledTableRow>
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    rowsPerPageOptions={[10, 20, 30, 60, 100]}
                    component="div"
                    count={totalCount !== undefined ? totalCount : ((data || []).length || 0)}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelDisplayedRows={({ page, count }) => `Page ${page + 1} of ${count !== -1 ? Math.ceil(count / rowsPerPage) : 1}`}
                />
            </TableContainer>
            
            <UploadCsvModal 
                open={isUploadModalOpen} 
                onClose={() => setIsUploadModalOpen(false)} 
                onUploadSuccess={onRefresh}
            />

            <Dialog open={isVerifyModalOpen} onClose={() => !verifying && setIsVerifyModalOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '8px', overflow: 'hidden' } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 24px', borderBottom: '1px solid #e0e0e0' }}>
                    <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#212529' }}>Bulk Mark as Verified</Typography>
                    <IconButton size="small" onClick={() => !verifying && setIsVerifyModalOpen(false)} sx={{ color: '#6c757d', '&:hover': { backgroundColor: 'rgba(0,0,0,0.05)' } }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>

                <Box sx={{ padding: '24px' }}>
                    <Box sx={{ display: 'flex', gap: '12px', backgroundColor: '#fff8eb', border: '1px solid #ffe0b2', borderRadius: '8px', padding: '16px', marginBottom: '20px' }}>
                        <ReportProblemOutlinedIcon sx={{ color: '#f57c00', fontSize: '22px', flexShrink: 0, marginTop: '2px' }} />
                        <Typography sx={{ fontSize: '14px', color: '#495057', lineHeight: 1.5 }}>
                            Please confirm that you have manually verified the Quiz, Concept, and Coding results, and have added and saved your feedback and justification for the <strong>{selectedRows.length}</strong> selected learner(s).
                        </Typography>
                    </Box>
                    <Typography sx={{ fontSize: '14px', color: '#495057', lineHeight: 1.6 }}>
                        Once you mark these <strong>{selectedRows.length}</strong> Practices as Verified, they will be locked, and you will no longer be able to make any changes to these learners' Practices.
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', padding: '16px 24px', backgroundColor: '#f8f9fa', borderTop: '1px solid #e0e0e0' }}>
                    <Button
                        onClick={() => setIsVerifyModalOpen(false)}
                        disabled={verifying}
                        sx={{ textTransform: 'none', fontWeight: 600, color: '#495057', padding: '6px 16px', borderRadius: '6px', '&:hover': { backgroundColor: 'rgba(0,0,0,0.05)' } }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleMarkAsVerified}
                        disabled={verifying}
                        sx={{ textTransform: 'none', fontWeight: 600, backgroundColor: '#0056b3', color: '#fff', padding: '6px 20px', borderRadius: '6px', '&:hover': { backgroundColor: '#004085' } }}
                    >
                        {verifying ? "Confirming..." : "Confirm"}
                    </Button>
                </Box>
            </Dialog>
        </Box>
    );
};

export default LearnerPracticesTable;
